/**
 * Central locale registry. Add a new file under src/locales and register it in
 * LOCALE_LOADERS. Optional: wire legacy codes in LOCALE_ALIASES.
 */

import enModule from './en';
import koModule from './ko';

/** @type {Record<string, object>} */
export const LOCALE_DICT = {
  en: enModule.default ?? enModule,
  ko: koModule.default ?? koModule,
};

const LOCALE_LOADERS = {
  ko: () => import('./ko'),
  en: () => import('./en'),
  ru: () => import('./ru'),
  vi: () => import('./vi'),
  'zh-CN': () => import('./zh-CN'),
  'zh-TW': () => import('./zh-TW'),
  ja: () => import('./ja'),
  hi: () => import('./hi'),
  th: () => import('./th'),
  id: () => import('./id'),
};

/**
 * Map legacy or alternate codes to the key used in LOCALE_LOADERS.
 * Add entries here when renaming locale files without breaking stored user prefs.
 */
const LOCALE_ALIASES = {
  zh: 'zh-CN',
};

export const SUPPORTED_LOCALE_CODES = Object.keys(LOCALE_LOADERS).sort();

/** @type {Record<string, Promise<object>>} */
const loadPromises = {};

/**
 * @param {string} [code]
 * @returns {boolean}
 */
export function isBundledLocale(code) {
  if (!code || typeof code !== 'string') return false;
  const c = String(code).trim();
  return Boolean(LOCALE_LOADERS[c]) || Boolean(LOCALE_ALIASES[c] && LOCALE_LOADERS[LOCALE_ALIASES[c]]);
}

/**
 * @param {string} [code]
 * @returns {keyof typeof LOCALE_LOADERS}
 */
export function resolveLocaleCode(code) {
  if (!code || typeof code !== 'string') return 'ko';
  const c = String(code).trim();
  if (LOCALE_LOADERS[c]) return c;
  if (LOCALE_ALIASES[c] && LOCALE_LOADERS[LOCALE_ALIASES[c]]) return LOCALE_ALIASES[c];
  return 'en';
}

/**
 * @param {string} [code]
 * @returns {Promise<object>}
 */
export async function loadLocale(code) {
  const resolved = resolveLocaleCode(code);
  if (LOCALE_DICT[resolved]) return LOCALE_DICT[resolved];

  const loader = LOCALE_LOADERS[resolved];
  if (!loader) {
    return loadLocale('en');
  }

  if (!loadPromises[resolved]) {
    loadPromises[resolved] = loader()
      .then((mod) => {
        const dict = mod.default ?? mod;
        LOCALE_DICT[resolved] = dict;
        if (resolved === 'zh-CN') LOCALE_DICT.zh = dict;
        return dict;
      })
      .catch((err) => {
        delete loadPromises[resolved];
        if (typeof console !== 'undefined' && console.warn) {
          console.warn(`[i18n] failed to load locale "${resolved}"`, err);
        }
        throw err;
      });
  }

  return loadPromises[resolved];
}

function buildChainFromCache(language) {
  const code = resolveLocaleCode(language);
  const primary = LOCALE_DICT[code] || LOCALE_DICT.en || LOCALE_DICT.ko;
  const chain = [primary, LOCALE_DICT.en, LOCALE_DICT.ko];
  const out = [];
  for (const d of chain) {
    if (d && !out.includes(d)) out.push(d);
  }
  return out;
}

/** Sync chain from cache only (empty until loadLocale / getDictionaryFallbackChain). */
export function getCachedDictionaryFallbackChain(language) {
  return buildChainFromCache(language);
}

/**
 * Dictionaries to try, in order, for missing keys.
 * Order: selected locale → English → Korean (last resort).
 * @param {string} [language] — raw stored code
 * @returns {Promise<object[]>}
 */
export async function getDictionaryFallbackChain(language) {
  const code = resolveLocaleCode(language);
  const codes = [...new Set([code, 'en', 'ko'])];
  await Promise.allSettled(codes.map((c) => loadLocale(c)));
  return buildChainFromCache(language);
}

/** Single locale dict (e.g. tests) — app `t()` uses getDictionaryFallbackChain. */
export async function getPrimaryDictionary(language) {
  const code = resolveLocaleCode(language);
  await loadLocale(code);
  return LOCALE_DICT[code] || LOCALE_DICT.ko;
}
